import { InputText } from "./Input";
import { Core } from "@easepick/core";
import { DateTime } from "@easepick/bundle";
import { AbstractFormElement } from "./Abstract";
import { PickerMonth } from "./PickerMonth";
import { addBlackoutTooltip, today } from "../../Modules/RentalAndAppointments/Helper/DateRangePicker";
import { preventScroll, releaseScroll } from "../../Utils/PreventScroll";

export abstract class PickerAbstract extends InputText {
    static SELECTORS = {
        trigger: ".trigger",
        input: "x-input",
        label: "label"
    };
    declare trigger: HTMLElement;
    declare easepick: Core;
    #cssPath: string = window.datePicker.cssPath;
    #cancel: string = window.datePicker.cancel;
    #apply: string = window.datePicker.apply;
    #lang: string = window.datePicker.lang;
    dateFormat: string = "YYYY-MM-DD";
    presentationFormat: string = "DD.MM.YYYY";
    abortController: AbortController;

    initElements() {
        super.initElements();

        if (this.hasAttribute("max")) {
            this.max = this.getAttribute("max");
        }
        if (this.hasAttribute("min")) {
            this.min = this.getAttribute("min");
        }
        if (this.hasAttribute("cancel")) {
            this.cancel = this.getAttribute("cancel");
        }
        if (this.hasAttribute("apply")) {
            this.apply = this.getAttribute("apply");
        }

        const id = Math.random().toString(36).slice(-6).toUpperCase();

        this.setAttribute("data-id", id);
        this.createCalendar(id);
        return this;
    }

    attachEvents() {
        this.label.addEventListener("click", () => {
            if (!this.disabled && this.easepick) {
                this.easepick.show();
            }
        });

        if (this.easepick) {
            this.easepick.ui.container.classList.add("centered");

            this.easepick.on("select", (e: CustomEvent) => {
                this.value = e.detail.date.format(this.dateFormat);
                this.dispatchEvent(new Event(this.mainEvent));
            });

            this.easepick.on("show", () => {
                this.abortController = new AbortController();
                preventScroll("blurred-bg");
                if (this.value) {
                    this.easepick.gotoDate(new DateTime(this.value));
                    this.easepick.setDate(new DateTime(this.value));
                } else {
                    this.easepick.gotoDate(new DateTime(this.max || today));
                }

                document.documentElement.addEventListener(
                    "keyup",
                    (e) => {
                        if (e.key === "Escape") {
                            this.easepick.hide();
                        }
                    },
                    { signal: this.abortController.signal }
                );
            });

            this.easepick.on("clear", () => {
                this.value = "";
                this.dispatchEvent(new Event(this.mainEvent));
            });

            this.easepick.on("hide", () => {
                releaseScroll("blurred-bg");
                this.abortController.abort();
            });

            this.easepick.on("view", (e) => {
                const { view, date, target } = e.detail;
                if (view === "CalendarDay" && this.disableHolidays) {
                    addBlackoutTooltip(view, target, date);
                }
            });
        }

        return super.attachEvents();
    }

    /**
     * validation
     */
    validate() {
        if (this.input.value && this.hasAttribute("allowed-days")) {
            const allowedDays = this.getAttribute("allowed-days")
                .split(",")
                .map((d) => parseInt(d));
            const selectedDay = new Date(this.value).getDay();
            this.input.setCustomValidity("");
            if (!allowedDays.includes(selectedDay)) {
                this.input.setCustomValidity("custom-error");
            }
        }
        const validationResult = super.validate();
        this.input.blur();
        return validationResult;
    }

    createTrigger() {
        this.trigger = document.createElement("div");
        this.trigger.classList.add("trigger");
        this.trigger.style.setProperty("display", "none");
        document.querySelector("body").appendChild(this.trigger);
    }

    abstract createCalendar(id: string): void;

    get value() {
        return this.input.value;
    }

    set value(v) {
        let value = "";
        let presentation = "";
        if (v) {
            value = new DateTime(v).format(this.dateFormat);
            presentation = new DateTime(v).format(this.presentationFormat);
        }
        this.input.value = value;
        this.querySelector<HTMLSpanElement>("span.input").dataset.formattedDate = presentation;
        this.label.classList.toggle("has-value", !!value);
        this.validate();
    }

    get minYear(): number {
        return +this.getAttribute("data-min-year");
    }

    get maxYear(): number {
        return +this.getAttribute("data-max-year");
    }

    get disableHolidays() {
        return this.getAttribute("data-disable-holidays") === "true";
    }

    get cssPath() {
        return this.#cssPath;
    }

    set cssPath(v) {
        this.#cssPath = v;
    }

    get lang() {
        return this.#lang;
    }

    set lang(v) {
        this.#lang = v;
    }

    get cancel() {
        return this.#cancel;
    }

    set cancel(v) {
        this.#cancel = v;
    }

    get apply() {
        return this.#apply;
    }

    set apply(v) {
        this.#apply = v;
    }

    get isMonthPicker(): boolean {
        return this instanceof PickerMonth;
    }
}

PickerAbstract.prototype.shadowStyle = document.createElement("template");
PickerAbstract.prototype.shadowStyle.innerHTML = `
${AbstractFormElement.prototype.shadowStyle.innerHTML}
<style>
input {
    display: none;
}
</style>
`;

PickerAbstract.prototype.template = document.createElement("template");
PickerAbstract.prototype.template.innerHTML = /*html*/ `
<label class="std-fe">
    <span class="label"></span>
    <span class="input"></span>
    <span class="icon calendar" role="presentation"></span>
</label>
<span class="error"></span>
<span class="help"></span>
<span class="trigger"></span>
`;
